import { Button, Flex, Radio, ScrollArea, Stack } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import { useState } from 'react';

import { TariffCard, type VacancyTariff } from '@entities/vacancy';

import { MODAL_SIZE_DEFAULT } from '@shared/ui/modal/constants';

import classes from './useSelectTariffModal.module.scss';

const MODAL_ID = 'select-vacancy-tariff';

type SelectTariffModalContentProps = {
  tariffs: VacancyTariff[];
  selectedTariffId: number | string | undefined;
  onConfirm: (tariffId: number) => void;
};

const SelectTariffModalContent = ({
  tariffs,
  selectedTariffId,
  onConfirm,
}: SelectTariffModalContentProps) => {
  const [pending, setPending] = useState(() => {
    if (selectedTariffId !== undefined && selectedTariffId !== '') {
      return String(selectedTariffId);
    }
    return tariffs[0] ? String(tariffs[0].id) : '';
  });

  const handleClose = () => {
    closeModal(MODAL_ID);
  };

  const handleConfirm = () => {
    if (pending) {
      onConfirm(Number(pending));
    }
    closeModal(MODAL_ID);
  };

  if (tariffs.length === 0) {
    return (
      <Flex justify='center' py='md'>
        Нет доступных тарифов
      </Flex>
    );
  }

  return (
    <Flex direction='column' gap={16} h='100%' mx={-16}>
      <ScrollArea className={classes.scrollArea}>
        <Radio.Group value={pending} onChange={setPending}>
          <Stack gap={16} px={16}>
            {tariffs.map(tariff => (
              <Radio.Card
                key={tariff.id}
                value={String(tariff.id)}
                p={16}
                radius={12}
                w='100%'
                withBorder={false}
                classNames={{ card: classes.tariffRadioCard }}
              >
                <Flex gap='md' align='flex-start' w='100%' wrap='nowrap'>
                  <Radio.Indicator
                    size='sm'
                    style={{
                      '--radio-color': 'var(--mantine-color-green-5)',
                    }}
                  />
                  <TariffCard
                    tariff={tariff}
                    selected={String(tariff.id) === pending}
                    embedded
                  />
                </Flex>
              </Radio.Card>
            ))}
          </Stack>
        </Radio.Group>
      </ScrollArea>

      <Flex gap={8} px={16}>
        <Button size='md' variant='gray' onClick={handleClose}>
          Отмена
        </Button>
        <Button size='md' onClick={handleConfirm} disabled={!pending}>
          Сохранить
        </Button>
      </Flex>
    </Flex>
  );
};

export type OpenSelectTariffModalParams = {
  tariffs: VacancyTariff[];
  selectedTariffId: number | string | undefined;
  onConfirm: (tariffId: number) => void;
};

export const useSelectTariffModal = () => {
  const openSelectTariffModal = ({
    tariffs,
    selectedTariffId,
    onConfirm,
  }: OpenSelectTariffModalParams) => {
    openModal({
      modalId: MODAL_ID,
      title: 'Выбор тарифа',
      size: MODAL_SIZE_DEFAULT,
      children: (
        <SelectTariffModalContent
          tariffs={tariffs}
          selectedTariffId={selectedTariffId}
          onConfirm={onConfirm}
        />
      ),
    });
  };

  return { openSelectTariffModal };
};
